import type { SupabaseClient, User } from '@supabase/supabase-js'
import { canonicalizeEmail } from '@applicator/shared'

import { extractOnboardingNamesFromAuthUser } from '../components/Onboarding/authProfile'

export type UserProfileRow = {
  first_name: string | null
  last_name: string | null
  phone: string | null
  email: string | null
  resume_file_path: string | null
  resume_md_path: string | null
  ai_provider: string | null
  ai_memory_file_path: string | null
  job_positions: string[] | null
  work_type: string | null
  location: string | null
  canonical_email: string | null
  created_at: string
  updated_at: string
}

/**
 * Load user_profile; if missing, seed a row from auth user (email + OAuth metadata names).
 */
export async function ensureUserProfileFromAuth(
  supabase: SupabaseClient,
  user: User,
): Promise<UserProfileRow | null> {
  const { data: existing, error: loadError } = await supabase
    .from('user_profile')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle()

  if (loadError && loadError.code !== 'PGRST116') {
    throw loadError
  }

  if (existing) {
    return existing as UserProfileRow
  }

  const { firstName, lastName } = extractOnboardingNamesFromAuthUser(user)
  const hasSeedData = Boolean(firstName || lastName || user.email)
  if (!hasSeedData) {
    return null
  }

  let canonicalEmail: string | null = null
  if (user.email) {
    try {
      canonicalEmail = canonicalizeEmail(user.email)
    } catch {
      canonicalEmail = null
    }
  }

  const payload = {
    user_id: user.id,
    first_name: firstName || null,
    last_name: lastName || null,
    email: user.email || null,
    canonical_email: canonicalEmail,
    phone: null,
    ai_provider: 'none' as const,
  }

  const { data: inserted, error: upsertError } = await supabase
    .from('user_profile')
    .upsert(payload, { onConflict: 'user_id' })
    .select('*')
    .single()

  if (upsertError) {
    throw upsertError
  }

  return inserted as UserProfileRow
}
